import type { Member } from "../../../utils.js";
import type { VoiceEntry, VoiceEntryWithUser } from "../types.js";

export type VoiceSessionMode = "with_others" | "self";

export type VoiceSessionParticipant = {
    userId: string;
    member: Member | null;
    startedAt: number;
    endedAt: number;
    duration: number;
    primary: boolean;
};

export type VoiceChannel = {
    id: string;
    name: string;
};

export type VoiceSessionListItem = {
    id: string;
    title: string;
    subtitle: string;
    channelId: string | null;
    subjectUserId: string;
    subjectMember: Member | null;
    startedAt: number;
    endedAt: number;
    duration: number;
    participants: VoiceSessionParticipant[];
};

export type VoiceCommonSessionEntry = {
    interacting_user: string;
    interacting_channel: string;
    interaction_start: number;
    interaction_end: number;
};

export type VoiceChannelSessionEntry = {
    userId: string;
    joinAt: number;
    leaveAt: number;
};

export function createSelfVoiceSessionItems(user: Member, entries: readonly VoiceEntry[]): VoiceSessionListItem[] {
    return entries.map((entry, index) => {
        const startedAt = Number(entry.interaction_start);
        const endedAt = Number(entry.interaction_end);
        const duration = Math.max(0, endedAt - startedAt);

        return {
            id              : `self:${startedAt}:${endedAt}:${index}`,
            title           : "Session vocale",
            subtitle        : "Présence vocale de l'utilisateur",
            channelId       : entry.interacting_channel ?? null,
            subjectUserId   : user.id,
            subjectMember   : user,
            startedAt,
            endedAt,
            duration,
            participants    : [{
                userId      : user.id,
                member      : user,
                startedAt,
                endedAt,
                duration,
                primary     : true,
            }],
        };
    });
}

export function createInteractionVoiceSessionItems(
    currentUserId: string,
    entries: readonly VoiceEntryWithUser[],
    members: ReadonlyMap<string, Member>,
): VoiceSessionListItem[] {
    return entries.map((entry, index) => {
        const endedAt = Number(entry.last_interaction_time);
        const duration = Math.max(0, Number(entry.total_interaction_time));
        const startedAt = Math.max(0, endedAt - duration);
        const participant = members.get(entry.interacting_user) ?? null;
        const title = participant?.serverNickname || participant?.user.globalName || participant?.user.username || "Utilisateur inconnu";

        return {
            id              : `with:${entry.interacting_user}:${endedAt}:${index}`,
            title,
            subtitle        : participant?.user.username || "Interaction vocale",
            channelId       : null,
            subjectUserId   : entry.interacting_user,
            subjectMember   : participant,
            startedAt,
            endedAt,
            duration,
            participants    : [
                {
                    userId      : currentUserId,
                    member      : null,
                    startedAt,
                    endedAt,
                    duration,
                    primary     : true,
                },
                {
                    userId      : entry.interacting_user,
                    member      : participant,
                    startedAt,
                    endedAt,
                    duration,
                    primary     : false,
                },
            ],
        };
    });
}

export function createCommonVoiceSessionItems(
    entries: readonly VoiceCommonSessionEntry[],
    channels: ReadonlyMap<string, VoiceChannel>,
    subjectUserId: string,
    subjectMember: Member | null,
): VoiceSessionListItem[] {
    return entries.map((entry, index) => {
        const startedAt = Number(entry.interaction_start);
        const endedAt = Number(entry.interaction_end);
        const duration = Math.max(0, endedAt - startedAt);
        const channel = channels.get(String(entry.interacting_channel));

        return {
            id              : `common:${entry.interacting_channel}:${startedAt}:${endedAt}:${index}`,
            title           : channel ? `#${channel.name}` : `Salon ${entry.interacting_channel}`,
            subtitle        : subjectMember?.user.username ?? "Session commune",
            channelId       : String(entry.interacting_channel),
            subjectUserId,
            subjectMember,
            startedAt,
            endedAt,
            duration,
            participants    : [],
        };
    });
}

export function createTimelineParticipants(
    entries: readonly VoiceChannelSessionEntry[],
    members: ReadonlyMap<string, Member>,
    session: VoiceSessionListItem,
    currentUserId: string,
): VoiceSessionParticipant[] {
    return entries.map((entry) => {
        const startedAt = Math.max(session.startedAt, Number(entry.joinAt));
        const endedAt = Math.min(session.endedAt, Number(entry.leaveAt));

        return {
            userId      : String(entry.userId),
            member      : members.get(String(entry.userId)) ?? null,
            startedAt,
            endedAt,
            duration    : Math.max(0, endedAt - startedAt),
            primary     : String(entry.userId) === currentUserId,
        };
    }).filter((participant) => participant.duration > 0)
        .sort((left, right) => {
            if (left.userId === currentUserId)
                return -1;

            if (right.userId === currentUserId)
                return 1;

            if (left.userId === session.subjectUserId)
                return -1;

            if (right.userId === session.subjectUserId)
                return 1;

            return right.duration - left.duration;
        });
}

export function getTimelineBounds(session: VoiceSessionListItem): { start: number; end: number; duration: number } {
    const start = Math.min(...session.participants.map((participant) => participant.startedAt), session.startedAt);
    const end = Math.max(...session.participants.map((participant) => participant.endedAt), session.endedAt);

    return {
        start,
        end,
        duration: Math.max(1, end - start),
    };
}
